<template>
  <div class="tv-display-container min-vh-100 bg-slate-950 text-white d-flex flex-column">
    <!-- Top Header Bar for TV Display -->
    <header class="tv-header px-4 py-3 d-flex justify-content-between align-items-center bg-slate-900 border-bottom border-slate-800 shadow-sm">
      <div class="d-flex align-items-center gap-3">
        <div class="tv-logo-circle d-flex align-items-center justify-content-center bg-teal text-white rounded-circle">
          <i class="fas fa-hospital-user fa-lg"></i>
        </div>
        <div>
          <h4 class="mb-0 font-weight-bold text-white tracking-wide">{{ facilityName }}</h4>
          <span class="text-xs text-teal font-weight-semibold">Live Consultation & Queue Monitor</span>
        </div>
      </div>

      <!-- Live Clock & Status Badge -->
      <div class="d-flex align-items-center gap-3">
        <div class="tv-stat-pill px-3 py-1 bg-slate-800 rounded-pill border border-slate-700 text-xs">
          <span class="pulse-indicator bg-success me-2"></span>
          <span>Live Display</span>
        </div>
        <div class="text-end">
          <div class="text-lg font-weight-bold text-white font-monospace">{{ currentTime }}</div>
          <div class="text-xxs text-slate-400">{{ currentDate }}</div>
        </div>
      </div>
    </header>

    <!-- Main Content Area: Active Calling / Counter Grid -->
    <main class="flex-grow-1 p-4 overflow-auto">
      <div class="row g-4">
        <!-- If no counters or tokens -->
        <div v-if="displayTokens.length === 0" class="col-12 text-center py-5">
          <div class="tv-empty-box p-5 rounded-4 bg-slate-900 border border-slate-800 mx-auto" style="max-width: 600px;">
            <i class="fas fa-ticket-alt fa-3x text-teal opacity-5 mb-3"></i>
            <h4 class="text-white font-weight-bold">No Active Tokens Currently</h4>
            <p class="text-sm text-slate-400 mb-0">Tokens will appear here in real-time as patients are called to counters.</p>
          </div>
        </div>

        <!-- Counter / Token Cards -->
        <div
          v-for="token in displayTokens"
          :key="token.id || token.counter_id"
          class="col-12 col-md-6 col-lg-4"
        >
          <div
            class="tv-counter-card h-100 p-4 rounded-4 bg-slate-900 border transition-all"
            :class="token.is_on_break || token.status === 'on_break' || token.counter_on_break ? 'border-warning shadow-warning-soft' : 'border-slate-800 shadow-md'"
          >
            <!-- Card Header: Counter & Doctor Details -->
            <div class="d-flex justify-content-between align-items-start mb-3 pb-2 border-bottom border-slate-800">
              <div>
                <span class="badge bg-slate-800 text-teal text-xs px-2 py-1 border border-slate-700 rounded-pill mb-1 d-inline-block">
                  <i class="fas fa-desktop me-1"></i>{{ token.counter_name || 'Counter Window' }}
                </span>
                <h5 class="text-white font-weight-bold mb-0 text-truncate" style="max-width: 220px;">
                  {{ token.doctor_name || token.token_attended || 'Duty Attendant' }}
                </h5>
                <small class="text-slate-400 text-xxs d-block">{{ token.service_name || token.department || 'General OPD' }}</small>
              </div>

              <!-- Status Badge -->
              <span
                v-if="token.is_on_break || token.status === 'on_break' || token.counter_on_break"
                class="badge bg-warning-soft text-warning text-xs px-2 py-1 font-weight-bold"
              >
                <i class="fas fa-coffee me-1"></i> On Break
              </span>
              <span
                v-else-if="token.status === 'calling'"
                class="badge bg-primary-soft text-primary text-xs px-2 py-1 font-weight-bold pulse-animation"
              >
                Calling
              </span>
              <span
                v-else
                class="badge bg-success-soft text-success text-xs px-2 py-1 font-weight-bold"
              >
                Serving
              </span>
            </div>

            <!-- Item 6: TV Break Banner When Doctor/Counter is On Break -->
            <div
              v-if="token.is_on_break || token.status === 'on_break' || token.counter_on_break"
              class="tv-break-banner p-3 bg-warning-soft text-dark rounded-3 mb-3 border border-warning"
            >
              <div class="font-weight-bold text-sm">
                <i class="fas fa-coffee me-2 text-warning"></i> Doctor on Break
              </div>
              <div class="text-sm mt-1">
                Resumes at <strong>{{ token.break_resumes_at || token.break_end || '14:00' }}</strong>
                <span v-if="token.remaining_break_minutes">
                  (in ~{{ token.remaining_break_minutes }} mins)
                </span>
              </div>
            </div>

            <!-- Main Token Number Display -->
            <div class="text-center py-3 my-2 bg-slate-950 rounded-3 border border-slate-800">
              <span class="text-xxs text-slate-400 text-uppercase tracking-wider font-weight-bold d-block mb-1">
                {{ token.is_on_break || token.status === 'on_break' || token.counter_on_break ? 'Paused Token' : 'Now Serving' }}
              </span>
              <div class="tv-token-number font-weight-bolder" :class="token.is_on_break ? 'text-slate-400 opacity-6' : 'text-teal'">
                {{ token.token_number || '--' }}
              </div>
              <div class="text-xs text-white font-weight-semibold mt-1 text-truncate px-2">
                {{ token.token_name || 'Patient' }}
              </div>
            </div>

            <!-- Waiting Queue Information -->
            <div class="d-flex justify-content-between align-items-center text-xxs text-slate-400 pt-2 border-top border-slate-800">
              <span>
                <i class="fas fa-users me-1 text-teal"></i>
                Waiting: <strong class="text-white">{{ token.waitingCount || 0 }}</strong>
              </span>
              <span v-if="token.next_token">
                Next: <strong class="text-teal font-weight-bold">{{ token.next_token }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- TV Footer Marquee / Notification Bar -->
    <footer class="tv-footer py-2 px-4 bg-slate-900 border-top border-slate-800 text-xs text-slate-400 d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center gap-2">
        <span class="badge bg-teal text-white text-xxs font-weight-bold px-2 py-1">NOTICE</span>
        <span class="text-truncate">Please proceed to your assigned counter when your token number is called. Thank you for your patience.</span>
      </div>
      <div class="d-flex align-items-center gap-3 text-nowrap">
        <span>Active Counters: <strong class="text-white">{{ displayTokens.length }}</strong></span>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "LiveTokens",
  data() {
    return {
      facilityName: localStorage.getItem("hospitalName") || localStorage.getItem("tenantName") || "Hospital Queue System",
      tokensList: [],
      countersList: [],
      currentTime: "",
      currentDate: "",
      timer: null,
      pollTimer: null
    };
  },
  computed: {
    apiBaseUrl() {
      const base = (process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_URL || "/api").replace(/\/+$/, "");
      return base.endsWith("/api") ? base : `${base}/api`;
    },
    displayTokens() {
      // Map counters and their current tokens + break status
      if (this.tokensList.length > 0) {
        return this.tokensList;
      }
      return this.countersList.map(c => {
        const isBreak = Boolean(
          c.is_on_break ||
          String(c.availability_status || c.status).toLowerCase().includes("break")
        );
        return {
          id: c.id,
          counter_id: c.id,
          counter_name: c.name,
          doctor_name: c.attendant_name || (c.user && c.user.name) || "Attendant",
          service_name: c.service_name || "General OPD",
          token_number: c.current_token || c.currentToken || "--",
          token_name: c.current_patient || "In Consultation",
          is_on_break: isBreak,
          counter_on_break: isBreak,
          status: isBreak ? "on_break" : (c.current_token ? "serving" : "idle"),
          break_start: c.break_start || "13:00",
          break_end: c.break_end || "14:00",
          break_resumes_at: c.break_resumes_at || c.break_end || "14:00",
          remaining_break_minutes: this.calculateRemainingBreakMinutes(c.break_end || "14:00"),
          waitingCount: 0
        };
      });
    }
  },
  mounted() {
    this.updateClock();
    this.timer = setInterval(this.updateClock, 1000);
    this.fetchData();
    this.pollTimer = setInterval(this.fetchData, 8000);
    window.addEventListener("doctor-availability-changed", this.handleAvailabilityChanged);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
    if (this.pollTimer) clearInterval(this.pollTimer);
    window.removeEventListener("doctor-availability-changed", this.handleAvailabilityChanged);
  },
  methods: {
    updateClock() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      this.currentDate = now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    },
    calculateRemainingBreakMinutes(breakEndStr) {
      if (!breakEndStr) return null;
      try {
        const now = new Date();
        const [endH, endM] = breakEndStr.split(":").map(Number);
        if (isNaN(endH) || isNaN(endM)) return null;
        const endTotal = endH * 60 + endM;
        const curTotal = now.getHours() * 60 + now.getMinutes();
        const diff = endTotal - curTotal;
        return diff > 0 ? diff : null;
      } catch (e) {
        return null;
      }
    },
    async fetchData() {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("auth_token") || "";
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // 1. Fetch counters with break info
        const cRes = await axios.get(`${this.apiBaseUrl}/v1/admin/counter`, { headers });
        const counters = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.data || []);
        if (Array.isArray(counters) && counters.length > 0) {
          this.countersList = counters;
        }

        // 2. Fetch live tokens if available
        try {
          const tRes = await axios.get(`${this.apiBaseUrl}/v1/admin/tokens`, {
            params: { status: "calling,serving,processing,pending" },
            headers
          });
          const tokens = Array.isArray(tRes.data) ? tRes.data : (tRes.data?.data || []);
          if (Array.isArray(tokens) && tokens.length > 0) {
            // Map tokens with counter break states
            this.tokensList = tokens.map(tok => {
              const matchedCounter = this.countersList.find(c => String(c.id) === String(tok.counter_id));
              const isCounterBreak = matchedCounter ? Boolean(
                matchedCounter.is_on_break ||
                String(matchedCounter.availability_status || matchedCounter.status).toLowerCase().includes("break")
              ) : false;

              const isBreak = Boolean(tok.is_on_break || tok.counter_on_break || isCounterBreak);
              const breakEnd = tok.break_end || matchedCounter?.break_end || "14:00";

              return {
                ...tok,
                is_on_break: isBreak,
                counter_on_break: isBreak,
                break_start: tok.break_start || matchedCounter?.break_start || "13:00",
                break_end: breakEnd,
                break_resumes_at: tok.break_resumes_at || breakEnd,
                remaining_break_minutes: tok.remaining_break_minutes || this.calculateRemainingBreakMinutes(breakEnd)
              };
            });
          }
        } catch (e) {
          // Tokens fallback to counters
        }
      } catch (err) {
        console.warn("TV Queue data fetch skipped:", err.message);
      }
    },
    handleAvailabilityChanged(e) {
      if (e.detail) {
        this.fetchData();
      }
    }
  }
};
</script>

<style scoped>
.tv-display-container {
  background-color: #0b1120;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
.tv-logo-circle {
  width: 44px;
  height: 44px;
  box-shadow: 0 0 15px rgba(13, 148, 136, 0.4);
}
.pulse-indicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 8px #22c55e;
}
.tv-counter-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}
.shadow-warning-soft {
  box-shadow: 0 0 25px rgba(245, 158, 11, 0.25) !important;
}
.tv-token-number {
  font-size: 3.5rem;
  line-height: 1;
  letter-spacing: -1px;
}
.tv-break-banner {
  background-color: #fef3c7;
  color: #92400e;
}
.bg-warning-soft {
  background-color: rgba(245, 158, 11, 0.15) !important;
}
.bg-primary-soft {
  background-color: rgba(37, 99, 235, 0.15) !important;
}
.bg-success-soft {
  background-color: rgba(34, 197, 94, 0.15) !important;
}
.bg-slate-900 {
  background-color: #0f172a !important;
}
.bg-slate-950 {
  background-color: #020617 !important;
}
.border-slate-800 {
  border-color: #1e293b !important;
}
.border-slate-700 {
  border-color: #334155 !important;
}
.text-slate-400 {
  color: #94a3b8 !important;
}
.text-teal {
  color: #0d9488 !important;
}
.bg-teal {
  background-color: #0d9488 !important;
}
.pulse-animation {
  animation: tvPulse 1.5s infinite;
}
@keyframes tvPulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.85; }
  100% { transform: scale(1); opacity: 1; }
}
</style>
